import { ChevronDown, X } from 'lucide-react'
import { useMemo } from 'react'

import type { OptimizationQualityEval } from '../../../lib/optimization/qualityEval'
import {
  collectQualityIssues,
  type QualityIssue,
} from '../../../lib/optimization/qualityEvalFlagging'
import type { SkillGapSummary } from '../../../lib/optimization/skillGapRecommendations'
import type { QualityGatePanelState } from '../../../lib/optimization/types'
import OptimizationFeedbackContent from './OptimizationFeedbackContent'

interface OptimizationQualityGateProps {
  panelState: QualityGatePanelState
  runId: string
  qualityEval: OptimizationQualityEval
  skillGapSummary?: SkillGapSummary | null
  qualityIssues?: QualityIssue[]
  liveSummaryProgrammaticPass?: boolean
  onMinimize: () => void
  onExpand: () => void
  onDismiss: () => void
}

export function qualityGateStatusLabel(
  qualityEval: OptimizationQualityEval,
  issues: QualityIssue[]
): string {
  const { primary_pass } = qualityEval
  const needsReview = !primary_pass && issues.length > 0
  return needsReview ? 'Needs review' : primary_pass ? 'Ready' : 'Needs review'
}

export default function OptimizationQualityGate({
  panelState,
  runId,
  qualityEval,
  skillGapSummary,
  qualityIssues,
  liveSummaryProgrammaticPass,
  onMinimize,
}: OptimizationQualityGateProps) {
  if (panelState === 'hidden' || panelState === 'minimized') return null

  return (
    <div
      role="region"
      aria-labelledby="quality-gate-title"
      data-testid="optimization-quality-gate"
      className="optimization-preview-dock-anchor flex w-full max-w-md max-h-[min(70vh,520px)] flex-col rounded-lg border border-border-default bg-surface-default shadow-xl"
    >
      <div className="flex shrink-0 items-start justify-between gap-2 border-b border-border-default px-4 py-3">
        <div className="min-w-0">
          <h2 id="quality-gate-title" className="text-base font-semibold text-text-primary">
            Tailoring results
          </h2>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            data-testid="optimization-quality-gate-minimize"
            onClick={onMinimize}
            className="rounded-md p-1.5 text-text-muted hover:bg-surface-muted hover:text-text-primary"
            aria-label="Minimize and review resume"
            title="Minimize and review resume"
          >
            <ChevronDown className="h-5 w-5" />
          </button>
          <button
            type="button"
            data-testid="optimization-quality-gate-close"
            onClick={onMinimize}
            className="rounded-md p-1.5 text-text-muted hover:bg-surface-muted hover:text-text-primary"
            aria-label="Collapse tailoring results to rail icon"
            title="Collapse to rail icon"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      <OptimizationFeedbackContent
        runId={runId}
        qualityEval={qualityEval}
        skillGapSummary={skillGapSummary}
        qualityIssues={qualityIssues}
        liveSummaryProgrammaticPass={liveSummaryProgrammaticPass}
        variant="modal"
        onRatingSaved={onMinimize}
      />

      <div className="flex shrink-0 justify-end gap-2 border-t border-border-default px-4 py-3">
        <button
          type="button"
          data-testid="optimization-quality-gate-minimize-footer"
          onClick={onMinimize}
          className="rounded-lg border border-border-default px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-muted"
        >
          Minimize — review resume
        </button>
      </div>
    </div>
  )
}

export function useQualityGateIssues(
  qualityEval: OptimizationQualityEval,
  qualityIssues?: QualityIssue[]
) {
  return useMemo(
    () => qualityIssues ?? collectQualityIssues(qualityEval),
    [qualityIssues, qualityEval]
  )
}
